// Edytor pism ODT w przeglądarce (Collabora Online na pełnym ekranie, protokół WOPI).
// Kontrakt z backendu: apps/doc_templates/editor.py + wopi.py.

/** Rodzaj slotu dokumentu edytowalnego — patrz doc_templates.editor.KINDS. */
export type EditorDocKind =
  | "generated"
  | "wpw_form"
  | "own_document"
  | "drive_file"
  | "issue_file"
  | "led_title_file"
  // List WPW (ścieżka listowna): pismo przewodnie i własna kopia formularza w liście.
  | "wpw_mail_cover"
  | "wpw_mail_form"
  // Zakładki „Pliki": projekt prowadzony przez podwykonawcę (LedFile; `led_tile_file` =
  // załącznik kafla z „Etapów" pokazany w „Plikach" jako projekcja) i zwykły projekt ZE
  // (ConnectionFile albo wpis z projekcji „<connection>:<managed>", zawsze do odczytu).
  | "led_file"
  | "led_tile_file"
  | "project_file";

/** Rodzaje „dowolny plik" (bez szablonu i pól ręcznych): dysk, załączniki, zakładki „Pliki". */
export const FREEFORM_EDITOR_KINDS: ReadonlySet<EditorDocKind> = new Set<EditorDocKind>([
  "drive_file", "issue_file", "led_title_file", "led_file", "led_tile_file", "project_file",
]);

/** Które rozszerzenia Collabora edytuje, a które tylko pokazuje (z discovery serwera). */
export interface EditorExtensions {
  edit: string[];
  view: string[];
}

export interface EditorAvailability {
  available: boolean;
  extensions?: EditorExtensions;
}

/** Rozszerzenie pliku (małe litery, bez kropki) albo "". */
export function fileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i + 1).toLowerCase() : "";
}

/** Rysunki CAD nie otwierają się w Collaborze, nawet gdy discovery je zgłasza — na razie
 *  tylko pobranie. Lustro `EXCLUDED_EXTENSIONS` z doc_templates/editor.py (backend i tak je
 *  wycina; tu na wypadek odpowiedzi sprzed zmiany w cache). */
export const EDITOR_EXCLUDED_EXTENSIONS: ReadonlySet<string> = new Set(["dxf", "dwg"]);

/** "edit" | "view" | null — czy edytor otworzy plik o tej nazwie. */
export function editorActionFor(name: string, ext?: EditorExtensions | null): "edit" | "view" | null {
  if (!ext) return null;
  const e = fileExtension(name);
  if (!e || EDITOR_EXCLUDED_EXTENSIONS.has(e)) return null;
  if (ext.edit.includes(e)) return "edit";
  if (ext.view.includes(e)) return "view";
  return null;
}

/** Rozszerzenia obrazów (zdjęcia, skany). Collabora zgłasza je w discovery jako „view", ale w
 *  zakładkach „Pliki" (projekt ZE i prowadzony projekt) zdjęcia otwierają się we własnym
 *  podglądzie — tym samym, co multimedia w GG (MediaLightbox), nie w edytorze (decyzja usera
 *  2026-10-08). Formaty, których przeglądarka nie wyświetli w <img> (TIFF, HEIC), zostają
 *  przy pobraniu. */
export const IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  "jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "avif",
]);

export function isImageFilename(name: string): boolean {
  return IMAGE_EXTENSIONS.has(fileExtension(name));
}

export type FilesTabOpenAction = "edit" | "view" | "image";

/** Jak otworzyć wpis zakładki „Pliki": zdjęcie → podgląd jak w GG (niezależnie od Collabory),
 *  inne formaty → Collabora wg discovery (`editorActionFor`), reszta → tylko pobranie. */
export function filesTabOpenAction(name: string, ext?: EditorExtensions | null): FilesTabOpenAction | null {
  return isImageFilename(name) ? "image" : editorActionFor(name, ext);
}

export interface EditorFileBrief {
  id: string;
  original_filename: string;
  url: string | null;
}

/** Stan slotu: NAJNOWSZA wersja + pola ręczne `#wpisz_…#` i to, co jeszcze siedzi w pliku. */
export interface EditorDocumentStatus {
  kind: EditorDocKind;
  /** Id, którym otwarto slot (dowolna wersja) — sesja dalej na nim działa po zapisie. */
  id: string;
  /** Id najnowszej wersji (po zapisie inny niż `id`). */
  current_id: string;
  label: string;
  filename: string;
  editable: boolean;
  /** Prawo zapisu: ACL (właściciel / edycja) i format, który Collabora umie zapisać. */
  can_write?: boolean;
  file: EditorFileBrief | null;
  /** Pola ręczne zadeklarowane w szablonie (`wpisz_…`). */
  manual_fields: string[];
  /** Znaczniki `#…#` wciąż w pliku — dopóki lista nie jest pusta, pakiet się nie skompiluje. */
  unfilled: string[];
  complete: boolean;
  editor_available?: boolean;
  // kontekst (zależnie od rodzaju)
  application_id?: string;
  application_number?: string;
  version?: number;
  template_kind?: string;
  // wpw_mail_cover / wpw_mail_form — list, którego część jest poprawiana
  letter_id?: string;
  // issue_file (załącznik zgłoszenia technicznego — zawsze tylko do odczytu)
  issue_id?: string;
  issue_title?: string;
  // led_file (zakładka „Pliki" prowadzonego projektu) / project_file (zakładka „Pliki" projektu ZE)
  led_file_id?: string;
  // led_tile_file (załącznik kafla z „Etapów" otwarty z zakładki „Pliki")
  tile_file_id?: number;
  milestone_id?: string;
  connection_id?: string;
  connection_file_id?: string | null;
  // led_title_file (załącznik zgłoszenia tytułu prawnego od firmy prowadzącej projekt ZE —
  // zawsze tylko do odczytu; dyrekcja ogląda dowód PRZED potwierdzeniem)
  report_id?: string;
  report_status?: "pending" | "confirmed" | "rejected";
  leadership_id?: string | null;
  parcel_teryt?: string;
  // drive_file
  node_id?: string;
  owner_name?: string;
  my_access?: "owner" | "edit" | "view" | null;
  pdf?: EditorFileBrief | null;
  /** PDF do kontroli robi się w tle (zapis WOPI oddaje od razu, żeby nie blokować
   *  Collabory) — dopóki to trwa, `pdf` bywa jeszcze pusty albo z POPRZEDNIEJ treści. */
  pdf_pending?: boolean;
  conversion_error?: string;
}

export interface EditorSession {
  /** Adres edytora Collabory z WOPISrc — cel formularza POST do <iframe>. */
  editor_url: string;
  access_token: string;
  /** Ważność tokenu (ms epoki) — Collabora chce ją w formularzu. */
  access_token_ttl: number;
  /** Origin SPA, z którym edytor rozmawia przez postMessage (filtr nadawcy). */
  postmessage_origin: string;
  document: EditorDocumentStatus;
}
